// Эвольвентная шестерня: профиль в торцевом сечении (мм, центр в начале координат) и расчёт пары.
// Модуль m — нормальный (как у косозубой фрезы); для прямых зубьев β = 0 и торцевой модуль тот же.
// Исходный контур — стандартный: головка 1·m, ножка 1,25·m (ISO 53).

export type Spec = {
  /** Модуль, мм. */ m: number;
  /** Число зубьев. */ z: number;
  /** Угол зацепления, градусы. */ alpha: number;
  /** Наклон зуба, градусы (0 — прямые). */ beta: number;
  /** Смещение профиля, доли модуля. */ x: number;
  /** Зазор в паре, мм по делительной окружности; каждая шестерня берёт половину. */ backlash: number;
};

const rad = (d: number) => (d * Math.PI) / 180;
const inv = (a: number) => Math.tan(a) - a;

/** Окружности шестерни. */
export function circles(g: Spec) {
  const b = rad(g.beta), mt = g.m / Math.cos(b), at = Math.atan(Math.tan(rad(g.alpha)) / Math.cos(b));
  const r = (mt * g.z) / 2;
  return { mt, at, r, rb: r * Math.cos(at), ra: r + g.m * (1 + g.x), rf: r - g.m * (1.25 - g.x) };
}

/** Межосевое расстояние пары (со смещением — по углу зацепления в работе). */
export function centerDistance(a: Spec, b: Spec) {
  const c = circles(a), a0 = (c.mt * (a.z + b.z)) / 2;
  const xs = a.x + b.x;
  if (Math.abs(xs) < 1e-9) return a0;
  const v = inv(c.at) + (2 * Math.tan(rad(a.alpha)) * xs) / (a.z + b.z);
  let w = c.at;
  for (let i = 0; i < 40; i++) { const t = Math.tan(w); w -= (t - w - v) / (t * t || 1e-12); w = Math.min(1.4, Math.max(0.01, w)); }
  return (a0 * Math.cos(c.at)) / Math.cos(w);
}

/** Меньше скольких зубьев (без смещения) зуб подрезается у основания, и какое смещение это убирает. */
export function undercut(g: Spec) {
  const zmin = 2 / Math.sin(rad(g.alpha)) ** 2, zv = g.z / Math.cos(rad(g.beta)) ** 3;
  return { zmin, xmin: (zmin - zv) / zmin };
}

/**
 * Контур шестерни против часовой. Зуб k смотрит по углу 2πk/z, первый — по +X.
 * Ниже основной окружности бок зуба идёт по радиусу до окружности впадин; скругление у основания
 * делается потом смещением контура.
 */
export function outline(g: Spec, flank = 10): [number, number][] {
  const { at, r, rb, ra, rf } = circles(g);
  const s = (circles(g).mt * Math.PI) / 2 + 2 * g.x * g.m * Math.tan(at) - g.backlash / 2;
  const psi = s / (2 * r);
  const th = (q: number) => psi + inv(at) - inv(Math.acos(Math.min(1, rb / Math.max(q, rb))));
  const r0 = Math.max(rb, rf);
  // вершина: если боки сходятся раньше наружной окружности — обрезаем там, где ширина зуба ещё ~0,1 мм
  let rt = ra;
  if (th(ra) * ra < 0.05) {
    let lo = r0, hi = ra;
    for (let i = 0; i < 50; i++) { const mid = (lo + hi) / 2; if (th(mid) * mid < 0.05) hi = mid; else lo = mid; }
    rt = lo;
  }
  const out: [number, number][] = [];
  const at2 = (q: number, a: number) => out.push([q * Math.cos(a), q * Math.sin(a)]);
  const step = (2 * Math.PI) / g.z, t0 = th(r0), tt = th(rt);
  const rs = Array.from({ length: flank + 1 }, (_, i) => r0 + ((rt - r0) * i) / flank);
  for (let k = 0; k < g.z; k++) {
    const f = k * step;
    if (rf < rb) at2(rf, f - t0);
    for (const q of rs) at2(q, f - th(q));
    for (let i = 1; i < 4; i++) at2(rt, f - tt + (2 * tt * i) / 4);
    for (let i = rs.length - 1; i >= 0; i--) at2(rs[i], f + th(rs[i]));
    if (rf < rb) at2(rf, f + t0);
    const gap = step - 2 * t0;
    if (gap > 0) for (let i = 1; i < 6; i++) at2(rf, f + t0 + (gap * i) / 6);
  }
  return out;
}
